import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Clock, TrendingUp, Briefcase, AlertTriangle, ChevronRight, ArrowLeft, X, Download, Pencil, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useVisibilityOverride } from "../context/VisibilityOverrideContext";
import { downloadFile } from "../lib/download";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function money(v) {
  return `£${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function ManagerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { includeExcluded } = useVisibilityOverride();
  return <ManagerDashboardInner navigate={navigate} user={user} includeExcluded={includeExcluded} />;
}

function ManagerDashboardInner({ navigate, user, includeExcluded }) {
  const [loading, setLoading] = useState(true);
  const [portfolio, setPortfolio] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [projectSearch, setProjectSearch] = useState("");
  const [personEntries, setPersonEntries] = useState(null);
  const [personLoading, setPersonLoading] = useState(false);
  const [editingHours, setEditingHours] = useState(false);
  const [hoursForm, setHoursForm] = useState({ prep: 0, delivery: 0 });
  const [hoursSaving, setHoursSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      if (includeExcluded) params.set("include_excluded", "true");
      const qs = params.toString() ? `?${params.toString()}` : "";

      const [portfolioRes, projectsRes] = await Promise.all([
        axios.get(`${API}/manager/portfolio${qs}`),
        axios.get(`${API}/manager/assigned-projects`),
      ]);
      setPortfolio(portfolioRes.data);
      setProjects(projectsRes.data.projects || []);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 403) {
        toast.error("No projects assigned. Ask your director/admin to assign projects.");
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Close detail panels when date range changes
  useEffect(() => { setSelectedProject(null); setProjectDetail(null); setPersonEntries(null); }, [dateRange.start, dateRange.end]);


  const fetchProjectDetail = async (pid) => {
    setDetailLoading(true);
    setSelectedProject(pid);
    setPersonEntries(null);
    setEditingHours(false);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/manager/project/${encodeURIComponent(pid)}/detail${qs}`);
      setProjectDetail(res.data);
      // Pre-fill hours form with current values
      setHoursForm({
        prep: res.data.summary?.prep_quoted || 0,
        delivery: res.data.summary?.delivery_quoted || 0,
      });
    } catch (error) {
      toast.error("Failed to load project detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const saveProjectHours = async () => {
    if (!selectedProject) return;
    setHoursSaving(true);
    try {
      await axios.put(`${API}/manager/project/${encodeURIComponent(selectedProject)}/hours`, {
        prep_hours: hoursForm.prep,
        delivery_hours: hoursForm.delivery,
      });
      toast.success("Project hours updated");
      setEditingHours(false);
      // Refresh both project detail and project list
      fetchProjectDetail(selectedProject);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update hours");
    } finally {
      setHoursSaving(false);
    }
  };

  const fetchPersonEntries = async (personnel) => {
    if (!selectedProject) return;
    setPersonLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.set("start_date", dateRange.start);
      if (dateRange.end) params.set("end_date", dateRange.end);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/manager/project/${encodeURIComponent(selectedProject)}/person/${encodeURIComponent(personnel)}${qs}`);
      setPersonEntries(res.data);
    } catch (error) {
      toast.error("Failed to load entries");
    } finally {
      setPersonLoading(false);
    }
  };

  if (loading && !portfolio) {
    return (<div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" /></div>);
  }

  const filteredProjects = projects.filter(p => {
    if (!projectSearch.trim()) return true;
    const term = projectSearch.toLowerCase();
    return `${p.project_id} ${p.company} ${p.client_name} ${p.status}`.toLowerCase().includes(term);
  });

  const overrunProjects = projects.filter(p => p.overrun > 0);
  const activeProjects = projects.filter(p => p.actual_hours > 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="manager-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="secondary" className="bg-purple-100 text-purple-800">Manager</Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Project Portfolio</h1>
          <p className="text-muted-foreground mt-1">{portfolio?.assigned_projects_count || 0} assigned projects · {portfolio?.team_size || 0} team members</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">From</Label>
            <Input type="date" value={dateRange.start} onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))} className="h-9 w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">To</Label>
            <Input type="date" value={dateRange.end} onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))} className="h-9 w-36" />
          </div>
        </div>
      </div>

      {/* Portfolio KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Assigned Projects</p>
            <p className="text-3xl font-bold tabular-nums">{portfolio?.assigned_projects_count || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Total Hours</p>
            <p className="text-3xl font-bold tabular-nums">{portfolio?.total_hours || 0}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Utilization</p>
            <p className="text-3xl font-bold tabular-nums">{portfolio?.utilization_rate || 0}%</p>
            <Progress value={portfolio?.utilization_rate || 0} className="mt-2 h-2" />
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 shadow-[0_1px_0_rgba(16,24,40,0.04)] hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-200">
          <CardContent className="pt-6">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">Team Members</p>
            <p className="text-3xl font-bold tabular-nums">{portfolio?.team_size || 0}</p>
          </CardContent>
        </Card>
        {overrunProjects.length > 0 && (
          <Card className="rounded-xl border border-red-200 bg-red-50 shadow-[0_1px_0_rgba(16,24,40,0.04)]">
            <CardContent className="pt-6">
              <p className="text-xs font-medium uppercase tracking-wider text-red-600 mb-1">Overrun Alert</p>
              <p className="text-3xl font-bold text-red-600 tabular-nums">{overrunProjects.length}</p>
              <p className="text-xs text-red-500 mt-1">projects exceeding quoted hours</p>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Projects List */}
        <div className="xl:col-span-7">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-semibold">Assigned Projects</CardTitle>
                  <CardDescription>Click a project to see detailed analysis</CardDescription>
                </div>
                <Input placeholder="Search projects..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} className="w-64" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[500px]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                      <th className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quoted</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Actual</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Remaining</th>
                      <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">People</th>
                      <th className="text-center py-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Health</th>
                      <th className="py-2 px-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProjects.map((p) => (
                      <tr
                        key={p.project_id}
                        className={`border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-[background-color] duration-150 ${selectedProject === p.project_id ? "bg-primary/5" : ""}`}
                        onClick={() => fetchProjectDetail(p.project_id)}
                      >
                        <td className="py-2.5 px-2">
                          <div>
                            <p className="font-medium truncate max-w-[200px]">{p.project_id}</p>
                            <p className="text-xs text-muted-foreground">{p.company || p.client_name}</p>
                          </div>
                        </td>
                        <td className="py-2.5 px-2">
                          <Badge variant="secondary" className="text-xs">{p.status || "N/A"}</Badge>
                        </td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{p.total_quoted_hours > 0 ? `${p.total_quoted_hours}h` : "-"}</td>
                        <td className="py-2.5 px-2 text-right tabular-nums font-medium">{p.actual_hours}h</td>
                        <td className="py-2.5 px-2 text-right">
                          {p.total_quoted_hours > 0 ? (
                            <span className={`tabular-nums font-medium ${p.overrun > 0 ? "text-red-600" : "text-emerald-600"}`}>
                              {p.overrun > 0 ? `+${p.overrun}h` : `${p.remaining_hours}h`}
                            </span>
                          ) : "-"}
                          {p.overrun > 0 && <AlertTriangle className="w-3 h-3 text-red-500 inline ml-1" />}
                        </td>
                        <td className="py-2.5 px-2 text-right tabular-nums">{p.personnel_count}</td>
                        <td className="py-2.5 px-2 text-center">
                          <div className={`inline-flex w-8 h-8 rounded-full items-center justify-center text-xs font-bold text-white ${
                            p.health_score >= 80 ? "bg-emerald-500" : p.health_score >= 60 ? "bg-amber-500" : "bg-red-500"
                          }`} title={p.risk_flags?.join(", ") || "Healthy"}>{p.health_score}</div>
                        </td>
                        <td className="py-2.5 px-1"><ChevronRight className="w-4 h-4 text-muted-foreground" /></td>
                      </tr>
                    ))}
                    {filteredProjects.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No projects assigned. Contact your director/admin.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Hours Breakdown Pie Chart — below Assigned Projects table */}
          {selectedProject && projectDetail && projectDetail.by_personnel?.length > 0 && (() => {
            const COLORS = ["#6366f1","#f59e0b","#10b981","#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316","#06b6d4","#84cc16"];
            const prepData = projectDetail.by_personnel.filter(p => p.prep_hours > 0).map(p => ({ name: p.personnel, value: p.prep_hours }));
            const deliveryData = projectDetail.by_personnel.filter(p => p.delivery_hours > 0).map(p => ({ name: p.personnel, value: p.delivery_hours }));
            const hasPrepData = prepData.length > 0;
            const hasDeliveryData = deliveryData.length > 0;
            if (!hasPrepData && !hasDeliveryData) return null;
            return (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Team Hours Breakdown — {selectedProject}</CardTitle>
                  <CardDescription>Who entered how much for prep vs delivery</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className={`grid gap-4 ${hasPrepData && hasDeliveryData ? "grid-cols-2" : "grid-cols-1"}`}>
                    {hasPrepData && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center mb-1">Prep Hours</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={prepData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name.split(' ')[0]} ${value}h`} labelLine={false}>
                              {prepData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v}h`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    {hasDeliveryData && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center mb-1">Delivery Hours</p>
                        <ResponsiveContainer width="100%" height={180}>
                          <PieChart>
                            <Pie data={deliveryData} cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2} dataKey="value" label={({ name, value }) => `${name.split(' ')[0]} ${value}h`} labelLine={false}>
                              {deliveryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip formatter={(v) => `${v}h`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>

        {/* Project Detail Panel */}
        <div className="xl:col-span-5">
          <Card className="sticky top-20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-semibold">Project Analysis</CardTitle>
                  <CardDescription>{selectedProject || "Select a project"}</CardDescription>
                </div>
                {selectedProject && (
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="gap-1" onClick={async () => {
                      try {
                        const exportParams = new URLSearchParams();
                        if (dateRange.start) exportParams.set("start_date", dateRange.start);
                        if (dateRange.end) exportParams.set("end_date", dateRange.end);
                        const exportQs = exportParams.toString() ? `?${exportParams.toString()}` : "";
                        await downloadFile(`${API}/export/excel/project/${encodeURIComponent(selectedProject)}${exportQs}`);
                        toast.success("Excel downloaded");
                      } catch { toast.error("Export failed"); }
                    }}>
                      <Download className="w-3.5 h-3.5" /> Excel
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setSelectedProject(null); setProjectDetail(null); setPersonEntries(null); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {detailLoading ? (
                <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
              ) : projectDetail ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Total Billed Hours</p>
                      <p className="text-xl font-bold tabular-nums">{projectDetail.summary.total_hours}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-xs text-muted-foreground">Value</p>
                      <p className="text-xl font-bold tabular-nums">{money(projectDetail.summary.project_value_gbp)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10">
                      <p className="text-xs text-muted-foreground">Billable</p>
                      <p className="text-xl font-bold text-emerald-600 tabular-nums">{projectDetail.summary.fee_hours}h</p>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-500/10">
                      <p className="text-xs text-muted-foreground">Non-Billable</p>
                      <p className="text-xl font-bold text-amber-600 tabular-nums">{projectDetail.summary.non_fee_hours}h</p>
                    </div>
                  </div>

                  {/* Quoted vs Actual — Editable */}
                  <div className="p-4 rounded-lg border border-border">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium">Quoted Hours (Prep & Delivery)</h4>
                      {!editingHours ? (
                        <Button variant="ghost" size="sm" className="gap-1 text-xs h-7" onClick={() => { setEditingHours(true); setHoursForm({ prep: projectDetail.summary.prep_quoted || 0, delivery: projectDetail.summary.delivery_quoted || 0 }); }}>
                          <Pencil className="w-3 h-3" /> Edit Hours
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setEditingHours(false)}>Cancel</Button>
                          <Button size="sm" className="gap-1 text-xs h-7" disabled={hoursSaving} onClick={saveProjectHours}>
                            <Check className="w-3 h-3" /> {hoursSaving ? "Saving..." : "Save"}
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingHours ? (() => {
                      const selectedP = projects.find(p => p.project_id === selectedProject);
                      const masterQuoted = selectedP?.total_quoted_hours || projectDetail.summary.total_quoted || 0;
                      const inputTotal = hoursForm.prep + hoursForm.delivery;
                      const diff = masterQuoted - inputTotal;
                      const isOver = diff < 0;
                      const isExact = diff === 0;
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-3 text-sm">
                            <span className="font-medium truncate max-w-[180px]">{selectedProject}</span>
                            {masterQuoted > 0 && <span className="text-muted-foreground">Quoted: <strong>{masterQuoted}h</strong></span>}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Prep Hours</Label>
                              <Input type="number" min="0" step="0.5" value={hoursForm.prep} onChange={(e) => setHoursForm(f => ({ ...f, prep: parseFloat(e.target.value) || 0 }))} className="h-9" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Delivery Hours</Label>
                              <Input type="number" min="0" step="0.5" value={hoursForm.delivery} onChange={(e) => setHoursForm(f => ({ ...f, delivery: parseFloat(e.target.value) || 0 }))} className="h-9" />
                            </div>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-medium tabular-nums">{inputTotal.toFixed(0)}h total</span>
                            {masterQuoted > 0 && (
                              <span className={`text-sm font-medium tabular-nums ${isExact ? "text-emerald-600" : isOver ? "text-red-600" : "text-amber-600"}`}>
                                {isExact ? "✓ exact" : isOver ? `+${Math.abs(diff).toFixed(0)}h over` : `${diff.toFixed(0)}h under`}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })() : projectDetail.summary.total_quoted > 0 ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Prep: {projectDetail.summary.prep_quoted}h quoted</span><span className="font-medium">{projectDetail.summary.prep_actual}h actual</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Delivery: {projectDetail.summary.delivery_quoted}h quoted</span><span className="font-medium">{projectDetail.summary.delivery_actual}h actual</span></div>
                        <div className="border-t pt-2 flex justify-between font-medium">
                          <span>Total: {projectDetail.summary.total_quoted}h quoted</span>
                          <span className={projectDetail.summary.overrun > 0 ? "text-red-600" : "text-emerald-600"}>
                            {projectDetail.summary.overrun > 0 ? `+${projectDetail.summary.overrun}h overrun` : `${projectDetail.summary.remaining_hours}h remaining`}
                          </span>
                        </div>
                        <Progress value={Math.min(100, (projectDetail.summary.total_hours / projectDetail.summary.total_quoted) * 100)} className="h-2" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <span className="text-amber-600 text-lg">⚠️</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">No quoted hours set</p>
                          <p className="text-xs text-amber-600">Click "Edit Hours" to set prep & delivery hours for this project</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Prep / Delivery Overrun Alerts */}
                  {projectDetail.summary.total_quoted > 0 && (() => {
                    const s = projectDetail.summary;
                    const prepOver = s.prep_quoted > 0 && s.prep_actual > s.prep_quoted;
                    const deliveryOver = s.delivery_quoted > 0 && s.delivery_actual > s.delivery_quoted;
                    if (!prepOver && !deliveryOver) return null;
                    return (
                      <div className="space-y-2">
                        {prepOver && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-800">Prep Hours Exceeded</p>
                              <p className="text-xs text-red-600">{s.prep_actual}h actual vs {s.prep_quoted}h quoted — <strong className="text-red-700">+{(s.prep_actual - s.prep_quoted).toFixed(1)}h over</strong></p>
                            </div>
                          </div>
                        )}
                        {deliveryOver && (
                          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-50 border border-red-200">
                            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-red-800">Delivery Hours Exceeded</p>
                              <p className="text-xs text-red-600">{s.delivery_actual}h actual vs {s.delivery_quoted}h quoted — <strong className="text-red-700">+{(s.delivery_actual - s.delivery_quoted).toFixed(1)}h over</strong></p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Team Members */}
                  <div>
                    <h4 className="text-sm font-medium mb-2">Team ({projectDetail.by_personnel?.length || 0}) - Click to see entries</h4>
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {(projectDetail.by_personnel || []).map(p => (
                        <div key={p.personnel}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-[background-color] duration-150 ${personEntries?.personnel === p.personnel ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30"}`}
                          onClick={() => fetchPersonEntries(p.personnel)}>
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-xs font-semibold text-primary">{p.personnel.split(" ").map(n => n[0]).join("")}</span>
                            </div>
                            <div>
                              <span className="text-sm font-medium">{p.personnel}</span>
                              <span className="text-xs text-muted-foreground ml-2">Prep: {p.prep_hours}h · Del: {p.delivery_hours}h</span>
                            </div>
                          </div>
                          <span className="text-sm tabular-nums font-medium">{p.total_hours}h</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Person Entries Detail */}
                  {personLoading && (
                    <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
                  )}
                  {personEntries && !personLoading && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="p-3 bg-primary/5 border-b border-border flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{personEntries.personnel} - Entries for this project</p>
                          <p className="text-xs text-muted-foreground">{personEntries.total_hours}h total · {personEntries.fee_hours}h billable · {personEntries.entries_count} entries</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setPersonEntries(null)}>Close</Button>
                      </div>
                      <div className="max-h-[250px] overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-background">
                            <tr className="border-b border-border">
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Date</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Description</th>
                              <th className="text-right py-1.5 px-2 font-semibold text-muted-foreground">Hours</th>
                              <th className="text-left py-1.5 px-2 font-semibold text-muted-foreground">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {personEntries.entries.map((e, i) => (
                              <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                                <td className="py-1.5 px-2 text-muted-foreground whitespace-nowrap">{e.date}</td>
                                <td className="py-1.5 px-2 max-w-[200px] truncate" title={e.description}>{e.description || "-"}</td>
                                <td className="py-1.5 px-2 text-right tabular-nums font-medium">{e.hours}</td>
                                <td className="py-1.5 px-2">
                                  <Badge className={`text-[10px] px-1.5 py-0 ${e.fee_earning?.includes("Non-Fee") ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                    {e.fee_earning?.includes("Delivery") ? "Delivery" : e.fee_earning?.includes("Prep") ? "Prep" : e.fee_earning?.includes("Non-Fee") ? "Non-Fee" : "Fee"}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Daily Trend */}
                  {projectDetail.daily_trend?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2">Daily Trend</h4>
                      <div className="h-[150px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={projectDetail.daily_trend} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(v) => { try { return format(new Date(v), "MMM d"); } catch { return v; } }} />
                            <YAxis tick={{ fontSize: 9 }} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 12 }} />
                            <Area type="monotone" dataKey="fee" name="Billable" stroke="#10B981" fill="#10B98130" />
                            <Area type="monotone" dataKey="non_fee" name="Non-Billable" stroke="#F59E0B" fill="#F59E0B30" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-muted-foreground">
                  <p>Select a project to view analysis</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Team Utilization - shows selected project team or all */}
      {(() => {
        const teamData = selectedProject && projectDetail?.by_personnel
          ? projectDetail.by_personnel.map(p => ({
              personnel: p.personnel,
              total_hours: p.total_hours,
              fee_hours: p.fee_hours,
              non_fee_hours: p.non_fee_hours,
              utilization_rate: p.total_hours > 0 ? Math.round(p.fee_hours / p.total_hours * 100 * 100) / 100 : 0,
              projects_count: 1,
            }))
          : (portfolio?.team_stats || []);
        const title = selectedProject
          ? `Team Performance — ${selectedProject}`
          : "Team Performance (Across Assigned Projects)";
        return teamData.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Consultant</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billable</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Non-Billable</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Utilization</th>
                    <th className="text-right py-2.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Projects</th>
                  </tr>
                </thead>
                <tbody>
                  {teamData.map(s => (
                    <tr key={s.personnel} className="border-b border-border/50 hover:bg-muted/30 transition-[background-color] duration-150">
                      <td className="py-2.5 px-3 font-medium">{s.personnel}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{s.total_hours}h</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-emerald-600">{s.fee_hours}h</td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-amber-600">{s.non_fee_hours}h</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 rounded-full bg-muted overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${s.utilization_rate}%`, backgroundColor: s.utilization_rate >= 50 ? '#10B981' : '#F59E0B' }} />
                          </div>
                          <span className="tabular-nums text-xs">{s.utilization_rate}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{s.projects_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null;
      })()}
    </div>
  );
}
